<template>
    <b-container fluid>
        <b-alert v-if="!productKode" show variant="warning" class="p-1">
            Simpan produk terlebih dahulu untuk menambahkan testimonial.
        </b-alert>
        <template v-else>
            <div class="d-flex justify-content-between align-items-center mb-2">
                <h5 class="m-0">Testimonial</h5>
                <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    size="sm"
                    @click="openCreateModal"
                >
                    <feather-icon icon="PlusIcon" class="mr-25" />
                    Add Testimonial
                </b-button>
            </div>

            <div v-if="isBusy" class="text-center text-danger my-2">
                <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
                <strong class="text-secondary">Loading...</strong>
            </div>

            <div v-else-if="!testimonials.length" class="text-center text-muted py-2">
                No testimonials available for this product.
            </div>

            <b-row v-else>
                <b-col
                    v-for="item in testimonials"
                    :key="item.id"
                    cols="12"
                    md="6"
                    class="mb-1"
                >
                    <b-card no-body class="border p-1 h-100">
                        <div class="d-flex">
                            <b-img
                                v-if="item.logo_url"
                                :src="item.logo_url"
                                rounded
                                width="48"
                                height="48"
                                class="mr-1"
                                style="object-fit: contain;"
                            />
                            <div class="flex-grow-1">
                                <strong>{{ item.customer }}</strong>
                                <p class="mb-0 mt-50" style="white-space: pre-wrap;">{{ item.testimonial }}</p>
                            </div>
                        </div>
                        <div class="d-flex justify-content-end mt-1">
                            <b-button
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                variant="secondary"
                                size="sm"
                                class="btn-icon mr-50"
                                @click="openEditModal(item)"
                            >
                                <feather-icon icon="Edit3Icon" />
                            </b-button>
                            <b-button
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                variant="danger"
                                size="sm"
                                class="btn-icon"
                                @click="confirmDelete(item)"
                            >
                                <feather-icon icon="Trash2Icon" />
                            </b-button>
                        </div>
                    </b-card>
                </b-col>
            </b-row>
        </template>

        <b-modal
            id="modal-testimonial-form"
            ref="modalTestimonialForm"
            :title="formMode === 'create' ? 'Add Testimonial' : 'Edit Testimonial'"
            hide-footer
            centered
            no-close-on-backdrop
            @hidden="resetForm"
        >
            <b-form @submit.prevent="submitForm">
                <b-form-group label-for="testimonial-customer">
                    <template slot="label">
                        Customer <strong class="text-danger">*</strong>
                    </template>
                    <b-form-input
                        id="testimonial-customer"
                        v-model="form.customer"
                        required
                        placeholder="Customer name"
                    />
                </b-form-group>
                <b-form-group label-for="testimonial-message">
                    <template slot="label">
                        Testimonial <strong class="text-danger">*</strong>
                    </template>
                    <b-form-textarea
                        id="testimonial-message"
                        v-model="form.testimonial"
                        required
                        rows="4"
                        placeholder="Testimonial message"
                    />
                </b-form-group>
                <b-form-group label-for="testimonial-logo">
                    <template slot="label">
                        Logo <strong v-if="formMode === 'create'" class="text-danger">*</strong>
                    </template>
                    <b-form-file
                        id="testimonial-logo"
                        v-model="form.logo"
                        accept="image/*"
                        placeholder="Drag and drop your file here or click to select"
                        :required="formMode === 'create'"
                    />
                    <small v-if="formMode === 'edit'" class="text-muted">
                        Kosongkan jika tidak ingin mengganti logo.
                    </small>
                </b-form-group>
                <div class="d-flex justify-content-end">
                    <b-button
                        type="button"
                        variant="light"
                        class="mr-1"
                        :disabled="submitting"
                        @click="$refs.modalTestimonialForm.hide()"
                    >
                        Cancel
                    </b-button>
                    <b-button type="submit" variant="primary" :disabled="submitting">
                        Save
                    </b-button>
                </div>
            </b-form>
        </b-modal>
    </b-container>
</template>

<script>
import {
    BContainer, BRow, BCol, BCard, BAlert, BButton, BSpinner, BImg,
    BModal, BForm, BFormGroup, BFormInput, BFormTextarea, BFormFile,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

export default {
    props: {
        productKode: String,
    },
    components: {
        BContainer,
        BRow,
        BCol,
        BCard,
        BAlert,
        BButton,
        BSpinner,
        BImg,
        BModal,
        BForm,
        BFormGroup,
        BFormInput,
        BFormTextarea,
        BFormFile,
    },
    directives: {
        Ripple,
    },
    data() {
        return {
            isBusy: false,
            submitting: false,
            testimonials: [],
            formMode: 'create',
            editingId: null,
            form: {
                customer: '',
                testimonial: '',
                logo: null,
            },
        }
    },
    watch: {
        productKode: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.fetchTestimonials()
                } else {
                    this.testimonials = []
                }
            },
        },
    },
    methods: {
        fetchTestimonials() {
            if (!this.productKode) return
            this.isBusy = true
            this.$http
                .get(`/product/testimonial/${this.productKode}`)
                .then(({ data }) => {
                    this.testimonials = data?.testimonials || []
                })
                .catch(err => {
                    this.$notif('Error', err?.data?.message || 'Gagal memuat testimonial', 'error')
                })
                .finally(() => {
                    this.isBusy = false
                })
        },
        openCreateModal() {
            this.formMode = 'create'
            this.editingId = null
            this.form = { customer: '', testimonial: '', logo: null }
            this.$refs.modalTestimonialForm.show()
        },
        openEditModal(item) {
            this.formMode = 'edit'
            this.editingId = item.id
            this.form = {
                customer: item.customer,
                testimonial: item.testimonial,
                logo: null,
            }
            this.$refs.modalTestimonialForm.show()
        },
        resetForm() {
            this.form = { customer: '', testimonial: '', logo: null }
            this.editingId = null
        },
        submitForm() {
            const formData = new FormData()
            formData.append('product_id', this.productKode)
            formData.append('customer', this.form.customer)
            formData.append('testimonial', this.form.testimonial)
            if (this.form.logo) {
                formData.append('logo', this.form.logo)
            }

            this.submitting = true

            let request
            if (this.formMode === 'create') {
                request = this.$http.post('/product/testimonial', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
            } else {
                formData.append('_method', 'PUT')
                request = this.$http.post(`/product/testimonial/${this.editingId}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
            }

            request
                .then(() => {
                    this.$notif(
                        'Success',
                        `Testimonial successfully ${this.formMode === 'create' ? 'added' : 'updated'}.`,
                        'success'
                    )
                    this.$refs.modalTestimonialForm.hide()
                    this.fetchTestimonials()
                })
                .catch(err => {
                    this.$notif('Error', err?.data?.message || 'Gagal menyimpan testimonial', 'error')
                })
                .finally(() => {
                    this.submitting = false
                })
        },
        confirmDelete(item) {
            this.$bvModal
                .msgBoxConfirm('Please confirm that you want to delete this testimonial.', {
                    title: 'Please Confirm',
                    size: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Yes',
                    cancelTitle: 'No',
                    cancelVariant: 'outline-secondary',
                    hideHeaderClose: false,
                    centered: true,
                })
                .then(value => {
                    if (value === true) {
                        this.$http
                            .delete(`/product/testimonial/${item.id}`)
                            .then(() => {
                                this.$notif('Success', 'Testimonial has been successfully deleted.', 'success')
                                this.fetchTestimonials()
                            })
                            .catch(err => {
                                this.$notif('Error', err?.data?.message || 'Gagal menghapus testimonial', 'error')
                            })
                    }
                })
        },
    },
}
</script>
